import { Grid, Typography } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { codeContainerStyles } from 'src/common/components/code-container/components/code-container-styles';
import ProductCodeContainer from 'src/common/components/code-container/components/product-code-container';
import {
  restorePage,
  setHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import barcode from 'src/common/media/barcode.svg';
import card from 'src/common/media/CreditDebit.svg';
import keypad from 'src/common/media/keypad.svg';
import { AppState } from 'src/common/redux/configure-store';
import { trainingModeStyles } from 'src/common/training-mode/training-mode-styles';
import { waitForCardDetails } from 'src/devices/payment/get-card-details';
import { cancelPinpadOperation } from 'src/devices/payment/pinpad-operations';
import { cancelLine } from 'src/home-page/business-logic/cancel-line-or-item';
import Keypad from 'src/keypad/components/keypad';
import { popupAsyncMessage, popupMessage } from 'src/messages/business-logic/popup-message';
import { compareBarcodeToLine } from '../business-logic/barcode-mismatch-void-item';

export const showVerifyOnlineItemContainer = async (
  onlineItems: IElectronicJournalRecord[],
): Promise<void> => {
  setHomePage(<VerifyOnlineItemContainer onlineItemList={onlineItems} />, data =>
    withSpinner(() => onVerify(data, onlineItems)),
  );
};

const onVerify = async (data: string, onlineItems: IElectronicJournalRecord[]): Promise<void> => {
  await compareBarcodeToLine(data, onlineItems)
    .then(itemToVoid => {
      cancelLine(itemToVoid);
    })
    .catch(err => {
      if (err.code === 'EmeraldItemNotFound') {
        const message: IMessageModal = {
          message: 'BarcodeMismatch',
          subMessage: 'BarcodeDoesntMatchItem',
          type: EmeraldDialogTypes.Error,
          size: MessageModalSizeEnum.Medium,
          dialogTestIdPrefix: 'online-item-barcode-mismatch',
          onAccept: popEmeraldModal,
          acceptButtonLabel: 'Ok',
          hideCancelButton: true,
          disableBackdropClick: true,
          modalId: 'BarcodeMismatchModal',
          dialogHeight: 54.427,
        };
        showEmeraldDialog(message);
      } else {
        popupMessage(err);
      }
    });
  onExit();
};

const onExit = () => restorePage();

export const VerifyOnlineItemContainer: React.FC<VerifyItemContainerProps> = ({
  onlineItemList,
}): JSX.Element => {
  const { t } = useTranslation();

  const keypadSubmitButton = {
    label: 'OK',
    onClick: data =>
      withSpinner(async () => {
        onVerify(data, onlineItemList);
      }),
  };

  const onCancel = async (): Promise<void> => {
    onExit();
  };

  const hasPinPad = useSelector((state: AppState) => state.pinPad.isPinpadConfigured);

  useEffect(() => {
    let needsCancel = true;
    (async () => {
      if (hasPinPad) {
        const details = await waitForCardDetails(1, 1)
          .catch(() => {
            return undefined;
          })
          .finally(() => {
            needsCancel = false;
          });
        const cardNumber = details?.card.pan;
        if (cardNumber) {
          withSpinner(async () => {
            onVerify(cardNumber, onlineItemList);
          });
        } else if (details) {
          await popupAsyncMessage({
            message: 'OnlineItemCardNumberError',
          });
        }
      }
    })();

    return () => {
      if (hasPinPad && needsCancel) {
        cancelPinpadOperation();
      }
    };
  }, [hasPinPad, onlineItemList]);

  return (
    <ProductCodeContainer
      headerTitle={t('VerifyItem')}
      onKeypadSubmit={keypadSubmitButton.onClick}
      keypadComponent={
        <Keypad
          keyboardId='verify-item'
          disableManualWeight
          disableQuantity
          topLabelText=' '
          submitButton={keypadSubmitButton}
          visible
          keypadTestIdPrefix='verify-item-'
        />
      }
    >
      <VerifyOnlineItem onCancel={onCancel} />
    </ProductCodeContainer>
  );
};

const VerifyOnlineItem = (props: VerifyOnlineItemProps): JSX.Element => {
  const classes = codeContainerStyles();
  const trainingModeClasses = trainingModeStyles();
  const { t } = useTranslation();
  const trainingMode = useSelector((state: AppState): boolean => state.posMode.trainingMode);

  return (
    <Grid
      item
      className={`fullHeight flexFull ${classes.outerMostContainer}`}
      data-testid='verify-item'
    >
      <Grid
        item
        className={`${
          trainingMode ? trainingModeClasses.trainingModeBackground : classes.background
        } fullWidth fullHeight`}
      >
        <Grid
          container
          direction='column'
          className={`fullHeight ${classes.outerFlexDesign}`}
          data-testid='verify-item-scanning-container'
        >
          <Grid
            item
            container
            className={classes.flexDesign}
            data-testid='verify-item-body-container'
          >
            <Grid item container className={classes.containerAlignment}>
              <Grid className={classes.spacing}>
                <Typography variant={'h4'} data-testid='verify-item-body-text1'>
                  {t('ItemScanMessage1')}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='center'
                className={classes.spacing}
                data-testid='verify-item-icon-1'
              >
                <Grid className={classes.iconPadding}>
                  <img src={barcode} alt='barcode' className={classes.icon} />
                </Grid>
                <img src={card} alt='card' className={classes.icon} />
              </Grid>
              <Grid container justifyContent='center' className={classes.spacing}>
                <Typography variant={'h4'} data-testid='verify-item-body-text2'>
                  - {t('ItemScanMessage2')} -
                </Typography>
              </Grid>
              <Grid container justifyContent='center' className={classes.spacing}>
                <Typography variant={'h4'} data-testid='verify-item-body-text3'>
                  {t('ItemScanMessage3')}
                </Typography>
              </Grid>
              <Grid
                container
                justifyContent='center'
                className={classes.spacing}
                data-testid='barcode-pinpad-icon-container'
              >
                <img src={keypad} alt='keypad' className={classes.icon} />
              </Grid>
            </Grid>
            <Grid item container justifyContent='center' className={classes.selfAlign}>
              <BaseButton
                buttonType='white'
                onClick={props.onCancel}
                data-testid='verify-item-cancel-button'
              >
                <Typography variant={'button'}>{t('Cancel')}</Typography>
              </BaseButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export interface VerifyOnlineItemProps {
  onCancel: () => void;
}

interface VerifyItemContainerProps {
  onlineItemList: IElectronicJournalRecord[];
}
